import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useChecklistState } from '../../onboarding/checklist/useChecklistState';
import { useChecklistAction } from '../../onboarding/checklist/useChecklistAction';
import { useChecklistVisibility } from '../../onboarding/checklist/useChecklistVisibility';
import { useFirstReleaseSummary } from '../../onboarding/checklist/useFirstReleaseSummary';
import { firstReleaseAccountId } from '../../onboarding/checklist/firstReleaseAccountId';
import { ChecklistSetupList } from '../../onboarding/checklist/ChecklistSetupList';
import { ChecklistDone } from '../../onboarding/checklist/ChecklistDone';
import { checklistMessages as messages } from '../../onboarding/checklist/checklistMessages';
import { useArtistSwitcher } from '../../accountSelector/useArtistSwitcher';
import { isDemoAccount, withoutDemoAccounts } from '../../accountSelector/demoAccounts';
import { SetupArtistNote } from './setup/SetupArtistNote';

/**
 * Settings -> Setup (design: onboarding d6). It shows the "Your first release" checklist of the
 * active artist with each body and button, and it is never empty (owner rule 2026-10-09):
 * - A member sees the list too (`setupChecklist`). The plan pick names the owner in place of its
 *   button, and only the owner gets "Hide" and "Show the checklist again".
 * - After a dismiss the list stays with "Show the checklist again" for the owner (design d6).
 * - A complete list shows the d4 line.
 * - With the demo artist (LaziiBee) active, a note says that it is an example artist and offers the
 *   own artist; the server answers the checklist of the first own artist below it.
 * - With no artist, a note offers "Add your artist".
 */
const SetupTabComponent: React.FC = () => {
  const intl = useIntl();
  const { state, isLoading, error, refetch, accountId } = useChecklistState();
  const runAction = useChecklistAction();
  const { dismiss, restore } = useChecklistVisibility(accountId);
  const summary = useFirstReleaseSummary(firstReleaseAccountId(state), state?.firstReleaseUuid);
  const switcher = useArtistSwitcher();
  const isDemoActive = isDemoAccount(switcher.active);
  const ownArtist = useMemo(() => {
    const first = withoutDemoAccounts(switcher.accounts ?? [])[0];
    return first === undefined ? null : { id: first.id, name: first.name ?? '' };
  }, [switcher.accounts]);

  if (isLoading) {
    return <LoadingView />;
  }
  if (state === undefined && error !== null && error !== undefined) {
    return (
      <ErrorView
        error={error}
        retry={() => {
          refetch().catch(console.error);
        }}
      />
    );
  }

  // An API from before 2026-10-09 has no `setupChecklist`: the Home list is the best it has.
  const checklist = state?.setupChecklist ?? state?.checklist ?? null;
  const canManage = state?.canManageChecklist ?? state?.isMember === false;
  const isDismissed = state?.checklistDismissed === true;
  const note =
    isDemoActive || checklist === null ? (
      <SetupArtistNote switcher={switcher} ownArtist={ownArtist} isDemoActive={isDemoActive} />
    ) : null;

  if (checklist === null) {
    return note;
  }

  let body: React.ReactNode;
  if (checklist.complete) {
    body = (
      <ChecklistDone
        songTitle={summary.title}
        onHide={canManage && !isDismissed ? dismiss : undefined}
        onShow={canManage && isDismissed ? restore : undefined}
      />
    );
  } else {
    const counts = { done: checklist.doneCount, total: checklist.total };
    let subtitle =
      summary.title === null
        ? intl.formatMessage(messages.subNoSong, counts)
        : intl.formatMessage(messages.sub, { ...counts, song: summary.title, when: summary.when });
    if (canManage && isDismissed) {
      subtitle = intl.formatMessage(messages.setupHidden);
    } else if (!canManage) {
      subtitle = `${subtitle} · ${intl.formatMessage(messages.setupMemberNote)}`;
    }
    body = (
      <ChecklistSetupList
        checklist={checklist}
        subtitle={subtitle}
        readyAssets={summary.readyAssets}
        onAction={runAction}
        canManage={canManage}
        onRestore={canManage && isDismissed ? restore : undefined}
      />
    );
  }

  return (
    <>
      {note}
      {body}
    </>
  );
};

export const SetupTab = TypedMemo(SetupTabComponent);
